import { useEffect } from 'react';
import { pushToDataLayer } from '../utils/gtm';

declare global {
  interface Window {
    Wistia?: {
      api: (mediaId: string) => WistiaVideo | undefined;
    };
    _wq?: Array<{
      id: string;
      onReady: (video: WistiaVideo) => void;
    }>;
  }
}

interface WistiaVideo {
  bind: (event: string, callback: () => void) => void;
  ready: (callback: () => void) => void;
}

export const useWistiaTracking = (mediaId: string): void => {
  useEffect(() => {
    let hasPlayed = false;

    const setupTracking = (video: WistiaVideo) => {
      video.bind('play', () => {
        if (!hasPlayed) {
          pushToDataLayer('video_play_offer');
          hasPlayed = true;
        }
      });
    };

    const trySetupWithApi = () => {
      if (window.Wistia) {
        const video = window.Wistia.api(mediaId);
        if (video) {
          setupTracking(video);
          return true;
        }
      }
      return false;
    };

    const setupWistiaTracking = (): void => {
      if (trySetupWithApi()) {
        return;
      }

      window._wq = window._wq || [];
      window._wq.push({
        id: mediaId,
        onReady: (video) => {
          setupTracking(video);
        },
      });
    };

    const timeoutId = setTimeout(() => {
      setupWistiaTracking();
    }, 1000);

    if (document.readyState === 'complete') {
      setupWistiaTracking();
    } else {
      window.addEventListener('load', setupWistiaTracking);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('load', setupWistiaTracking);
    };
  }, [mediaId]);
};
